import _ from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import ExpandablePanel from '../../../../../common/widgets/ExpandablePanel';
import Loader from '../../../../../common/widgets/Loader';
import { BenefitsContext } from '../../../contexts/BenefitsContext';
import { Category } from '../../../models/Categories';
import { ProductListModel } from '../../../models/Product';
import { resources } from '../../../resources/resources';

type CategoryRoot = { id: number; name: string; children: Category[] };

interface ICategoryFilter {
  products?: ProductListModel[];
  selectedCategoryIds: Set<number>;
  handleChange: (categoriesIds: Set<number>) => void;
}

const DiscoverCategoryFilter = (props: ICategoryFilter) => {
  const { t: translate } = useTranslation();
  const { benefitRoots } = useContext(BenefitsContext);
  const { selectedCategoryIds, handleChange, products } = props;

  const [categoryIds, setCategoryIds] =
    useState<Set<number>>(selectedCategoryIds);

  const categories = _.map(_.compact(_.values(benefitRoots)), category => {
    const firstLevelProductCategories = _.uniq(products?.map(p => p.path[1]));

    return {
      id: category.id,
      name: category.name,
      children: category?.children?.filter(child =>
        firstLevelProductCategories.some(id => id == child.id),
      ),
    } as CategoryRoot;
  });

  const selectCategory = (id: number) => {
    setCategoryIds(prevCategories => new Set(prevCategories).add(id));
  };

  const deselectCategory = (id: number) => {
    setCategoryIds(prevCategories => {
      const updatedCategories = new Set(prevCategories);
      updatedCategories.delete(id);
      return updatedCategories;
    });
  };

  useEffect(() => {
    setCategoryIds(selectedCategoryIds);
  }, [selectedCategoryIds]);

  useEffect(() => {
    handleChange(categoryIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIds.size]);

  return (
    <Loader
      loading={
        !benefitRoots.benefitClubCategoryRoot?.name &&
        !benefitRoots.flexibleBenefitsCategoryRoot?.name
      }
      loader={<Skeleton />}
    >
      <div className="action-panel">
        <div className="action-panel-title">
          <span>{translate(resources.filters.chooseCategory)}</span>
        </div>
        <div className="action-panel-content secondary-nav">
          <ul>
            {categories.map(ctgry => {
              if (ctgry.id && ctgry.children.length > 0) {
                return (
                  <li key={`${ctgry.id}_${ctgry.name}`} className={'nav-item'}>
                    <div style={{ width: '100%' }}>
                      <ExpandablePanel
                        expandOn={'header'}
                        title={ctgry.name}
                        isInitiallyExpanded={true}
                        titleClassName={
                          ctgry.children.some(c => categoryIds.has(c.id))
                            ? 'selected'
                            : ''
                        }
                      >
                        <ul>
                          {ctgry.children.map(child => {
                            return (
                              <li
                                key={`${child.id}_${child.name}`}
                                className={
                                  categoryIds.has(child.id)
                                    ? 'nav-item active'
                                    : 'nav-item'
                                }
                              >
                                <a
                                  onClick={() =>
                                    !categoryIds.has(child.id)
                                      ? selectCategory(child.id)
                                      : deselectCategory(child.id)
                                  }
                                >
                                  {child.name}
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      </ExpandablePanel>
                    </div>
                  </li>
                );
              }
            })}
          </ul>
        </div>
      </div>
    </Loader>
  );
};

export default DiscoverCategoryFilter;
